import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  FaChevronLeft,
  FaChevronRight,
  FaExpand,
  FaCompress,
} from 'react-icons/fa';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './PdfViewer.css';

// PDF.js worker is served as a static asset from /public so CRA doesn't need
// to bundle a worker. See public/pdf.worker.min.mjs.
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;

const SWIPE_THRESHOLD_PX = 50;

/**
 * PdfViewer — embedded PDF reader with:
 *   - per-page rendering (one page at a time)
 *   - prev/next buttons + editable page indicator
 *   - swipe gestures on touch devices
 *   - keyboard arrows when focused (← →)
 *   - fullscreen toggle (Escape to exit, ← → for navigation)
 *   - aspect-ratio-aware fitting (no horizontal clipping on narrow viewports)
 *   - slide animation on page change
 *
 * @param {string} url        URL of the PDF to render
 * @param {string} ariaLabel  ARIA label for the viewer region
 */
const PdfViewer = ({ url, ariaLabel = 'Visionneuse PDF' }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const [frameSize, setFrameSize] = useState({ w: 0, h: 0 });
  const [pageAspect, setPageAspect] = useState(null);
  const [direction, setDirection] = useState('next');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const frameRef = useRef(null);
  const prevPageRef = useRef(1);
  const touchStartRef = useRef({ x: 0, y: 0 });

  const toggleFullscreen = () => setIsFullscreen((v) => !v);

  // Track frame size for aspect-aware page fitting.
  useEffect(() => {
    if (!frameRef.current) return;
    const update = () => {
      if (!frameRef.current) return;
      setFrameSize({
        w: frameRef.current.clientWidth,
        h: frameRef.current.clientHeight,
      });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(frameRef.current);
    return () => ro.disconnect();
  }, []);

  // Sync page input with current page.
  useEffect(() => {
    setPageInput(String(pageNumber));
  }, [pageNumber]);

  // Track navigation direction for slide animation.
  useEffect(() => {
    if (pageNumber > prevPageRef.current) setDirection('next');
    else if (pageNumber < prevPageRef.current) setDirection('prev');
    prevPageRef.current = pageNumber;
  }, [pageNumber]);

  const goPrev = useCallback(
    () => setPageNumber((p) => Math.max(1, p - 1)),
    []
  );
  const goNext = useCallback(
    () => setPageNumber((p) => (numPages ? Math.min(numPages, p + 1) : p)),
    [numPages]
  );

  // Fullscreen: lock body scroll, listen for Escape + arrow keys globally.
  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsFullscreen(false);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isFullscreen, goPrev, goNext]);

  const onDocLoadSuccess = ({ numPages }) => setNumPages(numPages);

  const onPageLoadSuccess = (page) => {
    const vp = page.getViewport({ scale: 1 });
    const aspect = vp.height / vp.width;
    setPageAspect((prev) => (prev === aspect ? prev : aspect));
  };

  const onPageInputChange = (e) => setPageInput(e.target.value);
  const commitPageInput = () => {
    const n = parseInt(pageInput, 10);
    if (Number.isFinite(n) && n >= 1 && numPages && n <= numPages) {
      setPageNumber(n);
    } else {
      setPageInput(String(pageNumber));
    }
  };

  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartRef.current.x;
    const dy = t.clientY - touchStartRef.current.y;
    if (Math.abs(dx) > SWIPE_THRESHOLD_PX && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) goNext();
      else goPrev();
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goPrev();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      goNext();
    }
  };

  // Pick width vs height fit based on aspect ratios.
  const sizeProps = (() => {
    const { w, h } = frameSize;
    if (!w || !h) return null;
    if (pageAspect && h / w > pageAspect) return { width: w };
    return { height: h };
  })();

  return (
    <div className={`pdf-viewer-col ${isFullscreen ? 'is-fullscreen' : ''}`}>
      <div
        className="pdf-viewer-frame"
        ref={frameRef}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onKeyDown={onKeyDown}
        tabIndex={0}
        role="region"
        aria-label={ariaLabel}
      >
        <button
          type="button"
          className="pdf-viewer-fullscreen-btn"
          onClick={toggleFullscreen}
          aria-label={isFullscreen ? 'Quitter le plein écran' : 'Plein écran'}
          title={isFullscreen ? 'Quitter le plein écran (Échap)' : 'Plein écran'}
        >
          {isFullscreen ? <FaCompress /> : <FaExpand />}
        </button>

        <Document
          file={url}
          onLoadSuccess={onDocLoadSuccess}
          loading={
            <div className="pdf-viewer-state">
              <div className="pdf-viewer-spinner"></div>
              <span>Chargement du document...</span>
            </div>
          }
          error={
            <div className="pdf-viewer-state error">
              <span>Le document n'a pas pu être chargé.</span>
            </div>
          }
          noData={
            <div className="pdf-viewer-state">
              <span>Aucun document disponible.</span>
            </div>
          }
        >
          {sizeProps && (
            <div
              key={pageNumber}
              className="pdf-viewer-page-anim"
              data-direction={direction}
            >
              <Page
                pageNumber={pageNumber}
                onLoadSuccess={onPageLoadSuccess}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                {...sizeProps}
              />
            </div>
          )}
        </Document>
      </div>

      <div className="pdf-viewer-controls">
        <button
          type="button"
          onClick={goPrev}
          disabled={pageNumber <= 1}
          className="pdf-viewer-btn"
          aria-label="Page précédente"
        >
          <FaChevronLeft />
          <span className="btn-label">Précédent</span>
        </button>

        <div className="pdf-viewer-page-indicator">
          <input
            type="text"
            inputMode="numeric"
            value={pageInput}
            onChange={onPageInputChange}
            onBlur={commitPageInput}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitPageInput();
              }
            }}
            aria-label="Numéro de page"
            disabled={!numPages}
          />
          <span> / {numPages || '...'}</span>
        </div>

        <button
          type="button"
          onClick={goNext}
          disabled={!numPages || pageNumber >= numPages}
          className="pdf-viewer-btn"
          aria-label="Page suivante"
        >
          <span className="btn-label">Suivant</span>
          <FaChevronRight />
        </button>
      </div>

      <p className="pdf-viewer-hint">
        Astuce : glissez horizontalement pour changer de page.
      </p>
    </div>
  );
};

export default PdfViewer;
